import { Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivationEnd } from '@angular/router';

import { QuickBetConfig } from '@frontend/sports/common/client-config-data-access';
import { RouteTag } from '@frontend/sports/common/core/data-access/route';
import { CookieProvider } from '@frontend/sports/common/core/utils/cookie-provider';
import { getStoreValue } from '@frontend/sports/common/core/utils/redux';
import { filterSports } from '@frontend/sports/host-app/sports-product/feature/utils';
import { AdaptiveLayoutService } from '@frontend/sports/layout/feature';
import { ModalEventBridgeService } from '@frontend/sports/modal/feature';
import { MarketUrlParam } from '@frontend/sports/navigation/core/feature/url-helper';
import { SyncUserSettingsService } from '@frontend/sports/user/feature';
import { DeviceService, HtmlNode, TimerService, WINDOW } from '@frontend/vanilla/core';
import { Store } from '@ngrx/store';
import { filter, fromEvent, map, take } from 'rxjs';

import { ArcRestrictionService } from '../../../../lib/arc/arc-restriction.service';
import { BetBuilderConfigService } from '../../../../lib/bet-builder/services/bet-builder-config.service';
import { NavigationRouterService } from '../../../../lib/navigation/navigation-router.service';
import { PopupManager } from '../../../../lib/popup/popup-manager.service';
import { isSheetViewOpen } from '../../../../lib/ui-manager/ui-manager.state';
import { UiStates } from '../../../../lib/ui-manager/ui-states.enum';
import { IBetslipRootState } from '../../../base/store/state';
import { BetslipBetBuilderPick } from '../../../core/picks/betslip-bet-builder-pick';
import { BetslipGroupPick } from '../../../core/picks/betslip-group-pick';
import { BetslipPick } from '../../../core/picks/betslip-pick';
import { PickId, V2ParticipantPickId, V2WinPickId } from '../../../core/picks/pick-id';
import { getLegsCount } from '../../bet-builder/bet-builder-helper';
import { BetslipTypesActions } from '../../types/actions';
import { IBetslipTypeState } from '../../types/state';
import { QuickBetActions } from '../quick-bet-actions';
import { selectIsQuickBetOpen } from '../quick-bet.selectors';
import { IQuickBetState, QuickBetUiState } from '../quick-bet.state';

@Injectable()
export class QuickBetService {
    private readonly _window = inject(WINDOW);
    readonly minLegSelections = 2;
    readonly isQuickBetOpen = this.store.selectSignal(selectIsQuickBetOpen);
    private isSheetViewOpen = false;
    private readonly eventDetailsRoutes = [
        RouteTag.EventDetails,
        RouteTag.HorseRaceDetails,
        RouteTag.AntepostFixture,
        RouteTag.VirtualFixture,
        RouteTag.GreyhoundRaceDetails,
    ];

    private readonly livePageRoutes = [
        RouteTag.Live,
        RouteTag.LiveAll,
        RouteTag.LiveHighlights,
        RouteTag.LiveRegion,
        RouteTag.LiveSport,
        RouteTag.LiveVideo,
        RouteTag.LiveEsports,
        RouteTag.LiveLeague,
        RouteTag.LiveConference,
    ];

    private hideQuickBetByArc: boolean;

    constructor(
        private quickBetConfig: QuickBetConfig,
        private popupManager: PopupManager,
        private adaptiveLayoutService: AdaptiveLayoutService,
        private htmlNode: HtmlNode,
        private eventBridgeService: ModalEventBridgeService,
        private store: Store<IBetslipRootState>,
        private cookieProvider: CookieProvider,
        private arcRestrictionService: ArcRestrictionService,
        private betBuilderConfigService: BetBuilderConfigService,
        private navigationRouterService: NavigationRouterService,
        private userSettings: SyncUserSettingsService,
        private timerService: TimerService,
        private deviceService: DeviceService,
    ) {
        this.keepsQuickBetOpenWhenPageReloaded();
        this.arcRestrictionService.quickBetEnabled$.pipe(filterSports(), takeUntilDestroyed()).subscribe((state) => {
            this.hideQuickBetByArc = state;
            if (this.hideQuickBetByArc) {
                this.toggleQuickBetVisibility(false);
                this.toggleQuickBetBuilderDrawerVisibility(false);
            }
        });

        this.store
            .select(isSheetViewOpen)
            .pipe(filterSports(), takeUntilDestroyed())
            .subscribe((isSheetView: boolean) => (this.isSheetViewOpen = isSheetView));
    }

    dispatchSensitivePage(action: string): void {
        this.timerService.scheduleIdleCallback(() => {
            const isSensitive = action === 'open' ? 'true' : 'false';
            this.eventBridgeService.notifySensitivePage({
                isSensitiveForPushNotifications: 'false',
                isSensitiveForSliderGames: isSensitive,
                action,
                id: 'quickBet',
                type: '',
            });
        });
    }

    isQuickBetAllowed(event?: ActivationEnd, pick?: BetslipPick): boolean {
        if (!event) {
            return false;
        }

        if (!this.deviceService.isMobile) {
            return false;
        }

        if (this.isSheetViewOpen && this.checkQuickBetAvailability(false)) {
            return true;
        }

        const marketTab = event.snapshot.queryParams.market;
        if (this.isMyBetsTabOpen(marketTab)) {
            return false;
        }

        const routeTag = event.snapshot.data['tag'];

        return this.validateQuickBetAvailability(routeTag, pick);
    }

    validateQuickBetAvailability(routeTag: RouteTag, pick?: BetslipPick): boolean {
        const isRestricted = this.isRestricted(routeTag);

        const validRoute = routeTag !== RouteTag.MultiBuilder && routeTag !== RouteTag.Settings && routeTag !== RouteTag.MyBets;

        return this.checkQuickBetAvailability(isRestricted) && validRoute;
    }

    checkQuickBetAvailability(isRestricted: boolean): boolean {
        const isUserQuickBetEnabled = this.userSettings.bettingSettings.quickBetEnabled;

        return (
            ((!isRestricted && this.isQuickBetEnabled) || this.isAbQuickBetEnabled()) &&
            this.adaptiveLayoutService.current.quickBetCanBeShown &&
            !this.popupManager.isPopupOpen('betfinder') &&
            this.deviceService.isMobile &&
            !!isUserQuickBetEnabled
        );
    }

    isSupportedPickTypeId(pickId: PickId) {
        return !V2ParticipantPickId.isId(pickId) || V2WinPickId.isId(pickId);
    }

    isQuickBetParlayEnabled(): boolean {
        return this.quickBetConfig.isQuickBetParlayEnabled;
    }

    toggleQuickBetVisibility(quickBetActive: boolean): void {
        this.htmlNode.setCssClass('quick-bet-visible', quickBetActive);
        if (quickBetActive) {
            this.store.dispatch(QuickBetActions.showQuickBet());
        }
    }

    toggleQuickBetBuilderDrawerVisibility(quickBetDrawerActive: boolean): void {
        this.htmlNode.setCssClass('quick-bet-builder-visible', quickBetDrawerActive);
        if (!quickBetDrawerActive && !this.popupManager.isOpen()) {
            this.htmlNode.setCssClass('popup-on', quickBetDrawerActive);
        } else if (quickBetDrawerActive) {
            this.htmlNode.setCssClass('popup-on', quickBetDrawerActive);
        }
    }

    activationEndHandler(previous: ActivationEnd, current: ActivationEnd): void {
        const snapshot = current.snapshot;
        const routeConfig = snapshot && snapshot.routeConfig;
        const isLoginPath = routeConfig && routeConfig.path === 'login';

        if (isLoginPath && this.isQuickBetVisible) {
            this.store.dispatch(QuickBetActions.keepOpen({ open: true }));
        } else {
            if (this.initialRouteCheck(current)) {
                const isInEditBetMode = getStoreValue(this.store).ui.states.some((s) => s === UiStates.EditBet);
                if (this.quickBetState.keepOpen) {
                    this.store.dispatch(QuickBetActions.keepOpen({ open: false }));
                    if (
                        this.typeState.base.original.currentSelectedType &&
                        previous.snapshot.queryParams.popup !== 'betslip' &&
                        current.snapshot.queryParams.popup === 'betslip'
                    ) {
                        //when betslip open we should restore what was previous tab selected on betslip
                        this.store.dispatch(
                            BetslipTypesActions.setSelectedType({
                                betslipType: this.typeState.base.original.currentSelectedType,
                                selectedByUser: this.typeState.base.original.selectedByUser,
                                systemInfo: null,
                            }),
                        );
                        this.store.dispatch(BetslipTypesActions.setDefaultOriginalBase());
                    }
                } else if (isInEditBetMode && this.isQuickBetVisible) {
                    this.store.dispatch(QuickBetActions.hideQuickBet());
                } else if (!this.isQuickBetParlayEnabled()) {
                    this.closeQuickBet();
                }
            }
        }
    }

    initialRouteCheck(event: ActivationEnd | undefined) {
        if (!this.isQuickBetAllowed(event)) {
            this.closeQuickBet();

            return false;
        }

        return true;
    }

    eventDetailsExpandHandler(isExpanded: boolean): void {
        if (this.isQuickBetVisible) {
            if (isExpanded) {
                // We need to close the confirmation because when quick bet is restored
                // we can't get bet summary as the pick is already removed from betslip.
                if (this.quickBetState.uiState === QuickBetUiState.Success) {
                    this.store.dispatch(QuickBetActions.closeConfirmation());
                } else {
                    this.store.dispatch(QuickBetActions.hideQuickBet());
                }
            } else if (this.adaptiveLayoutService.current.columns < 3 && (this.isQuickBetEnabled || this.isAbQuickBetEnabled())) {
                if (this.isQuickBetParlayEnabled() && this.isQuickBetOpen()) {
                    this.store.dispatch(QuickBetActions.showQuickBet());
                } else {
                    this.store.dispatch(QuickBetActions.closeQuickBet({ cleanErrors: true }));
                }
            }
        }
    }

    isQuickBetBuilderAvailable(inOverlay: boolean, sportId?: number): boolean {
        if (inOverlay && this.betBuilderConfigService.isEntainUiOverlayEnabled()) {
            return true;
        }

        if (!inOverlay && this.hasRightColumn) {
            return false;
        }

        sportId ??= Number(this.navigationRouterService.activeNavigation);

        if (!sportId) {
            return false;
        }

        if (Number.isInteger(sportId) && sportId > 0) {
            return this.betBuilderConfigService.useBetBuilderInEntainUI(sportId) && this.adaptiveLayoutService.current.quickBetCanBeShown;
        }

        return false;
    }

    hasMinBetBuilderSelections(pick: BetslipBetBuilderPick | BetslipGroupPick | null): boolean {
        return getLegsCount(pick) >= this.minLegSelections;
    }

    get isQuickBetVisible(): boolean {
        return this.quickBetState.uiState !== QuickBetUiState.None;
    }

    get isQuickBetEnabled(): boolean {
        return this.hideQuickBetByArc ? false : this.quickBetConfig.isEnabled;
    }

    get getSelectedEventId(): string | undefined {
        return this.navigationRouterService.getSelectedEventId;
    }

    get hasRightColumn(): boolean {
        return this.htmlNode.hasCssClass('content-right');
    }

    private isRestricted(routeTag: RouteTag): boolean {
        if (this.quickBetConfig.showOnAllPages) {
            return false;
        }

        if (this.isEventDetailsRoute(routeTag)) {
            return !this.quickBetConfig.showOnEventDetails;
        }

        if (this.livePageRoutes.includes(routeTag)) {
            return !this.quickBetConfig.showOnLivePage;
        }

        return true;
    }

    private isEventDetailsRoute(routeTag: RouteTag): boolean {
        return this.eventDetailsRoutes.includes(routeTag);
    }

    private closeQuickBet(): void {
        if (this.isQuickBetVisible) {
            this.store.dispatch(QuickBetActions.closeQuickBet({ cleanErrors: true }));
        }
    }

    private get quickBetState(): IQuickBetState {
        return getStoreValue(this.store).betslip.quickBet;
    }

    private get typeState(): IBetslipTypeState {
        return getStoreValue(this.store).betslip.types;
    }

    private keepsQuickBetOpenWhenPageReloaded(): void {
        fromEvent(this._window, 'beforeunload')
            .pipe(
                map(() => this.quickBetState),
                filter((state) => state.uiState !== QuickBetUiState.None),
                take(1),
            )
            .subscribe(() => {
                this.store.dispatch(QuickBetActions.keepOpen({ open: true }));
            });
    }

    private isAbQuickBetEnabled(): boolean {
        return this.cookieProvider.getCookie('abTestQuickBetEnabled') === 'true';
    }

    private isMyBetsTabOpen(param: MarketUrlParam | undefined) {
        return param === MarketUrlParam.MyBets;
    }
}
